'use client'
export const dynamic = 'force-dynamic'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { PageHero } from '../../components/ui/kit'
import { businessScopeLabel } from '../../lib/business-scope-label'

type Feature = { id: string; text: string; included: boolean }
// Business geo-reach tiers (GET /api/business-tiers). Prices are REAL amounts; when the
// endpoint is degraded or empty the whole section hides — never a ₹0 / placeholder card.
type BizTier = {
  id: string; slug: string; name: string | null
  price: number | null; annualPrice: number | null
  scopeType: string | null; scopeValue: number | null; boundaryLevel: string | null
  badge: string | null; description: string | null
  features: Array<{ text?: string } | string> | null
}
type Tier = {
  id: string; name: string; slug: string; price: number; annualPrice?: number | null
  badge: string; color: string; textColor: string
  description: string; features: Feature[]
  sortOrder?: number
}

// Client-side fallback ONLY for when the tiers API is unreachable. Free-only by
// design — paid tiers must never appear from a fallback. The live data (and the
// server-side paidTiersPublic gate) comes from GET /api/subscription-tiers.
const BASIC_ONLY: Tier[] = [
  {
    id:'basic', name:'Masjid OS Basic', slug:'basic', price:0, annualPrice:0, badge:'BASIC',
    color:'#0E7C61', textColor:'white', description:'Granted when your masjid claim is approved', sortOrder:0,
    // Bullets deliberately empty: they live in subscription_tiers.features and this branch
    // runs precisely when we could not read them. Naming plan contents we have not fetched
    // would be a fabrication — and the old list was already wrong for this tier.
    features:[],
  },
]

export default function PricingPage() {
  const [billing, setBilling] = useState<'monthly'|'yearly'>('monthly')
  // Start Free-only so paid tiers never flash before the API resolves, and never
  // appear at all if the fetch fails. The API already applies the server-side
  // paidTiersPublic gate, so whatever it returns is safe to render as-is.
  const [tiers, setTiers] = useState<Tier[]>(BASIC_ONLY)
  const [liveMasjids, setLiveMasjids] = useState<number | null>(null)
  // Business tiers — null until loaded; [] or degraded → the whole section stays hidden.
  const [bizTiers, setBizTiers] = useState<BizTier[] | null>(null)

  useEffect(() => {
    let alive = true
    fetch('/api/subscription-tiers')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (alive && Array.isArray(d?.tiers) && d.tiers.length) {
          setTiers([...d.tiers].sort((a: Tier, b: Tier) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)))
        }
      })
      .catch(() => { /* keep Free-only fallback — never leak paid */ })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    fetch('/api/stats').then(r => r.ok ? r.json() : null).then(d => { if (d?.masjids) setLiveMasjids(d.masjids) }).catch(() => {})
  }, [])

  useEffect(() => {
    let alive = true
    fetch('/api/business-tiers')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!alive) return
        // Hide the section on degrade OR non-array: never a fabricated/₹0 price.
        setBizTiers(d && !d.degraded && Array.isArray(d.tiers) ? d.tiers : [])
      })
      .catch(() => { if (alive) setBizTiers([]) })
    return () => { alive = false }
  }, [])

  const activeTiers = tiers

  return (
    <div style={{ fontFamily:'Inter, sans-serif', minHeight:'100vh', background:'#F6F4EE' }}>

      {/* Canonical hero (DESIGN_SYSTEM §2) — one shared PageHero on every page */}
      <PageHero
        motif="pricing"
        eyebrow="Simple Pricing"
        title="Built for Indian Masjids"
        subtitle="Start free. Upgrade when your community grows. No hidden fees, no lock-in."
        actions={
          <div style={{ display:'inline-flex', background:'rgba(255,255,255,0.1)', borderRadius:12, padding:4 }}>
            {(['monthly','yearly'] as const).map(b=>(
              <button key={b} onClick={()=>setBilling(b)} style={{ padding:'8px 24px', borderRadius:9, border:'none', cursor:'pointer', fontSize:14, fontWeight:700, background:billing===b?'white':'transparent', color:billing===b?'#0B132B':'rgba(255,255,255,0.7)', transition:'all 0.2s' }}>
                {b==='monthly'?'Monthly':<>Yearly <span style={{ background:'#0E7C61', color:'white', borderRadius:6, padding:'1px 6px', fontSize:10, marginLeft:4, fontWeight:800 }}>2 MONTHS FREE</span></>}
              </button>
            ))}
          </div>
        }
      />

      {/* Tier cards — auto-fit so the row collapses to fewer/one column on
          mobile (same responsive pattern as the "for businesses" grid below)
          instead of forcing N columns at 390px. */}
      <div style={{ maxWidth: activeTiers.length === 1 ? 440 : 1100, margin:'24px auto 60px', padding:'0 20px', display:'grid', gridTemplateColumns: activeTiers.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap:16 }}>
        {activeTiers.map((tier, i) => {
          const yearlyPrice = tier.price === 0 ? 0 : (tier.annualPrice ?? tier.price * 10)
          const displayPrice = billing === 'yearly' ? yearlyPrice : tier.price
          const isPopular = i === 1
          return (
            <div key={tier.id} style={{ background:'white', borderRadius:20, padding:28, border:isPopular?`2px solid ${tier.color}`:'1px solid #eee', position:'relative', boxShadow:isPopular?`0 8px 32px ${tier.color}33`:'0 2px 8px rgba(0,0,0,0.04)', marginTop: isPopular ? 0 : 8 }}>
              {isPopular && (
                <div style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', background:tier.color, color:tier.textColor, borderRadius:20, padding:'4px 14px', fontSize:11, fontWeight:800, whiteSpace:'nowrap' }}>
                  MOST POPULAR
                </div>
              )}
              {/* Badge */}
              <div style={{ display:'inline-block', background:tier.color, color:tier.textColor, borderRadius:8, padding:'3px 12px', fontSize:11, fontWeight:800, marginBottom:12 }}>{tier.badge}</div>
              <div style={{ fontSize:15, fontWeight:700, color:'#0B132B', marginBottom:4 }}>{tier.name}</div>
              <div style={{ fontSize:13, color:'#888', marginBottom:14 }}>{tier.description}</div>
              {/* Price */}
              <div style={{ display:'flex', alignItems:'baseline', gap:4, marginBottom: billing==='yearly'&&tier.price>0 ? 4 : 16 }}>
                <span style={{ fontSize:34, fontWeight:800, color:tier.color }}>
                  {displayPrice === 0 ? 'Free' : `₹${displayPrice.toLocaleString('en-IN')}`}
                </span>
                {tier.price > 0 && <span style={{ fontSize:13, color:'#888' }}>/{billing==='yearly'?'year':'month'}</span>}
              </div>
              {billing==='yearly' && tier.price>0 && (
                <div style={{ fontSize:11, color:'#14B8A6', fontWeight:600, marginBottom:14 }}>
                  Save ₹{(tier.price*2).toLocaleString('en-IN')} — 2 months free!
                </div>
              )}
              <Link href="/onboard" style={{ display:'block', background:tier.color, color:tier.textColor, textAlign:'center', borderRadius:10, padding:'11px', fontSize:14, fontWeight:700, textDecoration:'none', marginBottom:20 }}>
                {tier.price === 0 ? 'Register Free' : 'Get Started'}
              </Link>
              {/* Features */}
              <div style={{ fontSize:11, fontWeight:700, color:'#888', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>
                What&apos;s included
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {tier.features.map(f => (
                  <div key={f.id} style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:13, color: f.included ? '#333' : '#bbb' }}>
                    <span style={{ flexShrink:0, marginTop:1 }}>{f.included ? '✅' : '❌'}</span>
                    <span style={{ lineHeight:1.4 }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* For Businesses — LIVE from /api/business-tiers. Hidden entirely when degraded/empty
          (bizTiers === null while loading, [] on failure) so no fabricated or ₹0 price ever
          shows. Prices, annual price, badge, description, scope and any feature bullets all
          come from the DB. Scope text is derived from scope_type/scope_value/boundary_level. */}
      {bizTiers && bizTiers.length > 0 && (
        <div style={{ maxWidth:1000, margin:'0 auto 60px', padding:'0 20px' }}>
          <h2 style={{ fontSize:22, fontWeight:800, color:'#0B132B', textAlign:'center', marginBottom:6 }}>For Businesses</h2>
          <p style={{ textAlign:'center', color:'#777', fontSize:13.5, marginBottom:22 }}>Committees pay for MasjidOS. Businesses pay for reach. Individuals never pay.</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:16 }}>
            {bizTiers.map((t) => {
              const free = !t.price || t.price === 0
              const scope = businessScopeLabel(t)
              const bullets = Array.isArray(t.features)
                ? t.features.map((f) => (typeof f === 'string' ? f : f?.text || '')).filter(Boolean)
                : []
              const annual = t.annualPrice != null && t.annualPrice > 0 ? t.annualPrice : null
              return (
                <div key={t.slug} style={{ background:'white', borderRadius:20, padding:24, border:`${free ? '1px solid #eee' : '2px solid #D4AF37'}`, display:'flex', flexDirection:'column' }}>
                  <div style={{ fontSize:12.5, fontWeight:800, color: free ? '#0E7C61' : '#9A7B1E', letterSpacing:'.06em', textTransform:'uppercase' }}>{t.badge || t.name || t.slug}</div>
                  <div style={{ fontSize:30, fontWeight:900, color:'#0B132B', margin:'8px 0 2px' }}>
                    {free ? '₹0' : `₹${Number(t.price).toLocaleString('en-IN')}`}
                    {!free && <span style={{ fontSize:13, fontWeight:700, color:'#888' }}>/mo</span>}
                  </div>
                  {annual ? <div style={{ fontSize:12, color:'#0E7C61', fontWeight:700, marginBottom:6 }}>₹{annual.toLocaleString('en-IN')}/yr · 2 months free</div> : <div style={{ marginBottom:6 }} />}
                  {t.name ? <div style={{ fontSize:15, fontWeight:800, color:'#0B132B', marginBottom:2 }}>{t.name}</div> : null}
                  {scope ? <div style={{ fontSize:12.5, fontWeight:700, color:'#1D4ED8', marginBottom:8 }}>{scope}</div> : null}
                  {t.description ? <p style={{ fontSize:13, color:'#666', lineHeight:1.5, margin:'0 0 12px' }}>{t.description}</p> : null}
                  {/* Bullets ONLY when the DB actually has them — no invented marketing. */}
                  {bullets.length > 0 && (
                    <ul style={{ margin:'0 0 16px', paddingLeft:18, fontSize:13, color:'#555', display:'grid', gap:6 }}>
                      {bullets.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  )}
                  <a href="/business/register" style={{ marginTop:'auto', display:'inline-block', textAlign:'center', background: free ? '#0E7C61' : '#0B132B', color:'white', borderRadius:10, padding:'10px 18px', fontSize:13.5, fontWeight:700, textDecoration:'none' }}>
                    {free ? 'Claim free' : 'List your business'}
                  </a>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* FAQ */}
      <div style={{ maxWidth:700, margin:'0 auto 60px', padding:'0 20px' }}>
        <h2 style={{ fontSize:22, fontWeight:800, color:'#0B132B', textAlign:'center', marginBottom:24 }}>Common Questions</h2>
        {[
          { q:'Can I switch plans anytime?', a:'Yes, upgrade or downgrade at any time. Changes take effect at the next billing cycle.' },
          { q:'Is there a contract or lock-in?', a:'No contracts. Month-to-month on all paid plans. Cancel anytime with no penalties.' },
          { q:'How do I pay?', a:'We accept UPI, cards, net banking via Razorpay. Yearly billing gives 2 months free.' },
          { q:'Do you offer discounts for NGOs?', a:'Yes — registered Muslim NGOs and charitable trusts get 20% off on all paid plans. Contact us.' },
          { q:'What happens to my data if I cancel?', a:'Your masjid profile stays on the Free plan. Your data is always yours.' },
        ].map((faq,i) => (
          <details key={i} style={{ background:'white', borderRadius:12, padding:'16px 20px', marginBottom:10, border:'1px solid #eee', cursor:'pointer' }}>
            <summary style={{ fontSize:15, fontWeight:700, color:'#0B132B', listStyle:'none', display:'flex', justifyContent:'space-between' }}>
              {faq.q} <span style={{ color:'#888' }}>+</span>
            </summary>
            <p style={{ margin:'12px 0 0', fontSize:14, color:'#555', lineHeight:1.6 }}>{faq.a}</p>
          </details>
        ))}
      </div>

      {/* CTA */}
      <div style={{ background:'linear-gradient(135deg,#0E7C61,#0B8A6D)', padding:'48px 20px', textAlign:'center' }}>
        <h2 style={{ fontSize:24, fontWeight:900, color:'white', margin:'0 0 12px' }}>Ready to get started?</h2>
        <p style={{ color:'rgba(255,255,255,0.8)', fontSize:15, margin:'0 0 24px' }}>Join {liveMasjids ? liveMasjids.toLocaleString('en-IN') : 'the'} masjids already on Masjids.in</p>
        <Link href="/onboard" style={{ background:'white', color:'#0E7C61', borderRadius:12, padding:'14px 36px', fontSize:16, fontWeight:700, textDecoration:'none', display:'inline-block' }}>
          Register Your Masjid Free →
        </Link>
      </div>

    </div>
  )
}
